// Import jQuery (available from vendor bundle)
import $ from 'jquery';

// Helper to safely invoke SimpleAuth methods when globals might be swapped out
function safeCallSimpleAuth(methodName, ...args) {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const auth = window.SimpleAuth;
  if (!auth || typeof auth !== 'object') {
    const consoleObj = typeof console !== 'undefined' ? console : null;
    if (consoleObj) {
      const logFn = consoleObj.debug || consoleObj.info || consoleObj.log || (() => {});
      logFn.call(consoleObj, `SimpleAuth: '${methodName}' skipped – SimpleAuth unavailable in current window`);
    }
    return undefined;
  }

  const fn = auth[methodName];
  if (typeof fn !== 'function') {
    const consoleObj = typeof console !== 'undefined' ? console : null;
    if (consoleObj) {
      const logFn = consoleObj.debug || consoleObj.info || consoleObj.log || (() => {});
      logFn.call(consoleObj, `SimpleAuth: '${methodName}' skipped – method missing or not a function`);
    }
    return undefined;
  }

  try {
    return fn.apply(auth, args);
  } catch (error) {
    console.error(`SimpleAuth: '${methodName}' threw`, error);
    return undefined;
  }
}

window.userData = window.userData || {};
const SIMPLE_AUTH_DEBUG = window.localStorage && window.localStorage.getItem('askfelix.debugAuth') === 'true';

function debugAuthLog(...args) {
  if (SIMPLE_AUTH_DEBUG) {
    console.debug(...args);
  }
}

function isSubscribedUser(userData) {
  if (!userData || typeof userData !== 'object') {
    return false;
  }

  if (userData.is_subscribed === true || userData.isSubscribed === true) {
    return true;
  }

  try {
    return localStorage.getItem('isSubscribed') === 'true';
  } catch (_) {
    return false;
  }
}

function updateHeaderElements(selector, hidden, hideInline = false) {
  if (typeof document === 'undefined' || typeof document.querySelectorAll !== 'function') {
    return;
  }

  let elements;
  try {
    elements = document.querySelectorAll(selector);
  } catch (_) {
    return;
  }

  for (const element of elements) {
    if (element && element.classList && typeof element.classList.toggle === 'function') {
      element.classList.toggle('auth-hidden', Boolean(hidden));
    }
    if (hideInline && element && element.style) {
      element.style.display = 'none';
    }
  }
}

function syncHeaderAuthControls(userData) {
  const isLoggedIn = !!(userData && userData.secret);
  const isSubscribed = isLoggedIn && isSubscribedUser(userData);

  window.__headerAuthState = {
    isLoggedIn,
    isSubscribed
  };

  // Header state is simple DOM work and must not depend on a mutable global `$`.
  // Some WebViews provide a non-jQuery `$`, whose collections lack toggleClass.
  updateHeaderElements('.header-login-signup', isLoggedIn);
  updateHeaderElements('.header-subscribe', !isLoggedIn || isSubscribed);
  updateHeaderElements('.header-logout-link', !isLoggedIn);
  updateHeaderElements('.header-account', !isLoggedIn);

  if (!isLoggedIn) {
    updateHeaderElements('.header-team-selector', false, true);
  }
}

window.syncHeaderAuthControls = syncHeaderAuthControls;

// Simple client-side auth state management
window.SimpleAuth = {
  // Check if user is logged in from localStorage
  isLoggedIn: function () {
    try {
      // Detailed debugging of localStorage access
      const allKeys = [];
      for (let i = 0; i < localStorage.length; i++) {
        allKeys.push(localStorage.key(i));
      }

      const userEmail = localStorage.getItem('userEmail');
      const secret = localStorage.getItem('secret');
      const hasEmail = userEmail !== null && userEmail !== 'null';
      const hasSecret = secret !== null && secret !== 'null';

      debugAuthLog('SimpleAuth.isLoggedIn() detailed check:', {
        hasEmail,
        hasSecret,
        email: userEmail,
        secret: secret ? '***' : null,
        allLocalStorageKeys: allKeys,
        localStorageLength: localStorage.length
      });

      return hasEmail && hasSecret;
    } catch (e) {
      console.error('SimpleAuth.isLoggedIn() localStorage access error:', e);
      return false;
    }
  },

  // Get user data from localStorage
  getCurrentUser: function () {
    if (!this.isLoggedIn()) {
      return null;
    }

    const userData = {
      email: localStorage.getItem('userEmail'),
      name: localStorage.getItem('userName') || localStorage.getItem('userEmail').split('@')[0],
      secret: localStorage.getItem('secret'),
      uid: localStorage.getItem('secret'), // Use secret as uid for simplicity
      id: localStorage.getItem('secret'), // Use secret as id for compatibility with existing code
      is_subscribed: localStorage.getItem('isSubscribed') === 'true'
    };

    // Always ensure window.userData is set
    window.userData = userData;
    window.user = userData; // Maintain legacy global for compatibility
    syncHeaderAuthControls(userData);

    return userData;
  },

  // Login with email/password
  login: function (email, password) {
    const emailTrimmed = typeof email === "string" ? email.trim() : email;

    return fetch('/api/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: emailTrimmed,
        password: password
      })
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Login failed');
        }
        return response.json();
      })
      .then(userData => {
        // Store user data in localStorage
        localStorage.setItem('userEmail', userData.email);
        localStorage.setItem('userName', userData.name || userData.email.split('@')[0]);
        localStorage.setItem('secret', userData.secret);
        localStorage.setItem('isSubscribed', userData.is_subscribed || false);

        console.log('SimpleAuth.login: Data stored in localStorage:', {
          email: userData.email,
          secret: userData.secret ? '***' : null,
          name: userData.name
        });

        // Ensure compatibility - add id field if missing
        if (!userData.id && userData.secret) {
          userData.id = userData.secret;
        }

        // Force initialization to ensure userData is set properly
        window.firebaseUserDataLocked = true;
        window.userData = userData;
        window.user = userData;
        window.userFullyLoaded = true;
        syncHeaderAuthControls(userData);

        // Trigger user ready event
        console.log('SimpleAuth.login: Triggering user-ready event with userData:', userData);
        // Native event for modern listeners
        document.dispatchEvent(new CustomEvent('user-ready', { detail: userData }));
        // jQuery event for legacy listeners expecting (event, userData)
        if (typeof window.$ !== 'undefined' && window.$ && window.$.fn) {
          try { window.$(document).trigger('user-ready', userData); } catch (_) {
            // jQuery not available, skip trigger
          }
        }

        // Lock userData to prevent Firebase from overriding it
        setTimeout(() => {
          safeCallSimpleAuth('lockUserData');
        }, 100);

        return userData;
      });
  },

  // Sign up with email/password - supports multiple call signatures for backward compatibility
  signup: function (...rawArgs) {
    const isEmail = value => typeof value === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    let name = null;
    let email;
    let password;

    if (rawArgs.length === 1 && typeof rawArgs[0] === 'object' && rawArgs[0] !== null) {
      // Signature: signup({ email, password, name })
      const payload = rawArgs[0];
      name = payload.name || null;
      email = payload.email;
      password = payload.password;
    } else if (rawArgs.length === 3) {
      if (isEmail(rawArgs[0]) && !isEmail(rawArgs[1])) {
        // Signature: signup(email, password, name)
        email = rawArgs[0];
        password = rawArgs[1];
        name = rawArgs[2] || null;
      } else {
        // Signature: signup(name, email, password)
        name = rawArgs[0] || null;
        email = rawArgs[1];
        password = rawArgs[2];
      }
    } else if (rawArgs.length === 2 && isEmail(rawArgs[0])) {
      // Signature: signup(email, password)
      email = rawArgs[0];
      password = rawArgs[1];
    }

    if (!email || !password) {
      const error = new Error('Signup requires email and password');
      return Promise.reject(error);
    }

    if (typeof email === 'string') {
      email = email.trim();
    }

    return fetch('/api/signup', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: email,
        password: password,
        name: name || null
      })
    })
      .then(response => {
        if (!response.ok) {
          // Check if it's a "Email already registered" error (409)
          if (response.status === 409) {
            const error = new Error('Email already registered');
            error.status = 409;
            error.email = email;
            error.password = password;
            throw error;
          }
          throw new Error('Signup failed');
        }
        return response.json();
      })
      .then(userData => {
        // Store user data in localStorage
        localStorage.setItem('userEmail', userData.email);
        localStorage.setItem('userName', userData.name || userData.email.split('@')[0]);
        localStorage.setItem('secret', userData.secret);
        localStorage.setItem('isSubscribed', userData.is_subscribed || false);

        console.log('SimpleAuth.signup: Data stored in localStorage:', {
          email: userData.email,
          secret: userData.secret ? '***' : null,
          name: userData.name
        });

        // Ensure compatibility - add id field if missing
        if (!userData.id && userData.secret) {
          userData.id = userData.secret;
        }

        // Force initialization to ensure userData is set properly
        window.firebaseUserDataLocked = true;
        window.userData = userData;
        window.user = userData;
        window.userFullyLoaded = true;
        syncHeaderAuthControls(userData);

        // Trigger user ready event
        console.log('SimpleAuth.signup: Triggering user-ready event with userData:', userData);
        document.dispatchEvent(new CustomEvent('user-ready', { detail: userData }));
        if (typeof window.$ !== 'undefined' && window.$ && window.$.fn) {
          try { window.$(document).trigger('user-ready', userData); } catch (_) {
            // jQuery not available, skip trigger
          }
        }

        // Lock userData to prevent Firebase from overriding it
        setTimeout(() => {
          safeCallSimpleAuth('lockUserData');
        }, 100);

        return userData;
      });
  },

  // Logout
  logout: function () {
    return fetch('/api/logout', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    }).then(() => {
      // Clear localStorage
      localStorage.removeItem('userEmail');
      localStorage.removeItem('userName');
      localStorage.removeItem('secret');
      localStorage.removeItem('isSubscribed');

      // Clear global state
      window.userData = {};
      window.user = {};
      window.userFullyLoaded = true;
      syncHeaderAuthControls({});

      // Trigger user ready event with empty data
      document.dispatchEvent(new CustomEvent('user-ready', { detail: {} }));
      if (typeof window.$ !== 'undefined' && window.$ && window.$.fn) {
        try { window.$(document).trigger('user-ready', {}); } catch (_) {
          // jQuery not available, skip trigger
        }
      }

      // Stay on the current page (SPA) and refresh UI state instead of forcing navigation
      try {
        if (typeof window.updateAuthUI === 'function') {
          window.updateAuthUI();
        }
      } catch (_) { /* ignore */ }

      if (typeof window.showSnackbar === 'function') {
        window.showSnackbar('Signed out', 'info');
      }
    });
  },

  // Initialize auth state on page load
  init: function () {
    console.log('SimpleAuth.init() called');
    window.userFullyLoaded = false;

    if (this.isLoggedIn()) {
      // User is logged in
      const userData = this.getCurrentUser();
      console.log('SimpleAuth: User is logged in', userData);
      window.userData = userData;
      window.user = userData;
      syncHeaderAuthControls(userData);

      // Defer jQuery operations until jQuery is available
      if (typeof $ !== 'undefined') {
        // Update UI elements
        $('.subscribe-uid').val(userData.secret);
        $('.subscribe-secret').val(userData.secret);
        $('.checkout-button').attr('disabled', false);
      } else {
        // Queue jQuery operations for when it's available
        window.addEventListener('load', function () {
          if (typeof $ !== 'undefined') {
            syncHeaderAuthControls(userData);

            // Update UI elements
            $('.subscribe-uid').val(userData.secret);
            $('.subscribe-secret').val(userData.secret);
            $('.checkout-button').attr('disabled', false);
          }
        });
      }

      // Only redirect from login page to app page when we actually have a logged-in user
      if (window.location.pathname === '/login' && userData && userData.secret) {
        // Preserve redirect target if present
        try {
          const params = new URLSearchParams(window.location.search);
          const next = params.get('next');
          window.location.href = next || '/app';
        } catch (_) {
          window.location.href = '/app';
        }
      }

      window.userFullyLoaded = true;
      console.log('SimpleAuth: Triggering user-ready event with userData:', userData);
      document.dispatchEvent(new CustomEvent('user-ready', { detail: userData }));
      if (typeof window.$ !== 'undefined' && window.$ && window.$.fn) {
        try { window.$(document).trigger('user-ready', userData); } catch (_) {}
      }
    } else {
      // User is not logged in
      console.log('SimpleAuth: User is not logged in');
      syncHeaderAuthControls({});
      if (typeof $ === 'undefined') {
        window.addEventListener('load', function () {
          syncHeaderAuthControls({});
        });
      }

      window.userFullyLoaded = true;
      document.dispatchEvent(new CustomEvent('user-ready', { detail: {} }));
      if (typeof window.$ !== 'undefined' && window.$ && window.$.fn) {
        try { window.$(document).trigger('user-ready', {}); } catch (_) {
          // jQuery not available, skip trigger
        }
      }

      // Redirect to login if on protected pages
      const protectedPaths = ['/app', '/admin'];
      if (protectedPaths.includes(window.location.pathname)) {
        // Redirect to login with next param to return after auth
        const currentUrl = encodeURIComponent(window.location.href);
        window.location.href = `/login?next=${currentUrl}`;
      }
    }
  },

  // Force override any existing auth systems
  forceInit: function () {
    console.log('SimpleAuth: Force initializing...');

    // Disable Firebase auth state listeners to prevent conflicts
    if (window.firebase && window.firebase.auth) {
      try {
        // Store reference to original Firebase auth to disable listeners
        const auth = window.firebase.auth();

        // Sign out any existing Firebase user
        auth.signOut();

        // Override Firebase auth state detection to prevent conflicts
        // Store original onAuthStateChanged
        if (!auth._originalOnAuthStateChanged) {
          auth._originalOnAuthStateChanged = auth.onAuthStateChanged;
        }

        // Replace onAuthStateChanged with a no-op when SimpleAuth is active
        auth.onAuthStateChanged = function (callback) {
          console.log('SimpleAuth: Blocking Firebase onAuthStateChanged listener');
          // Return unsubscribe function that does nothing
          return function () {};
        };

        console.log('SimpleAuth: Firebase auth listeners disabled');
      } catch (e) {
        console.log('Firebase auth disable failed (expected if not loaded):', e);
      }
    }

    // Prevent any existing Firebase user-ready events from overriding our data
    window.firebaseUserDataLocked = true;

    // Ensure our userData takes precedence
    this.init();

    // Re-trigger user-ready after a short delay to override any Firebase events
    setTimeout(() => {
      if (this.isLoggedIn()) {
        const userData = this.getCurrentUser();
        window.userData = userData;
        window.user = userData;
        window.userFullyLoaded = true;
        syncHeaderAuthControls(userData);
        debugAuthLog('SimpleAuth: Re-triggering user-ready event with userData:', userData);
        document.dispatchEvent(new CustomEvent('user-ready', { detail: userData }));
        if (typeof window.$ !== 'undefined' && window.$ && window.$.fn) {
          try { window.$(document).trigger('user-ready', userData); } catch (_) {
            // jQuery not available, skip trigger
          }
        }
      }
    }, 100);

    // Final check after shorter delay to avoid long timeouts
    setTimeout(() => {
      debugAuthLog('SimpleAuth: Final retry check...');
      if (this.isLoggedIn()) {
        const userData = this.getCurrentUser();
        window.userData = userData;
        window.user = userData;
        window.userFullyLoaded = true;
        syncHeaderAuthControls(userData);
        debugAuthLog('SimpleAuth: Final retry - setting userData:', userData);
        document.dispatchEvent(new CustomEvent('user-ready', { detail: userData }));
        if (typeof window.$ !== 'undefined' && window.$ && window.$.fn) {
          try { window.$(document).trigger('user-ready', userData); } catch (_) {
            // jQuery not available, skip trigger
          }
        }
      } else {
        debugAuthLog('SimpleAuth: Final retry - still not logged in, localStorage:', {
          userEmail: localStorage.getItem('userEmail'),
          secret: localStorage.getItem('secret') ? '***' : null
        });
      }
    }, 300);

    // Lock userData to prevent Firebase from overriding it
    setTimeout(() => {
      if (this.isLoggedIn()) {
        this.lockUserData();
      }
    }, 500);
  },

  // Lock userData to prevent external overrides
  lockUserData: function () {
    if (window.userData && window.userData.secret) {
      console.log('SimpleAuth: Locking userData to prevent external modifications');

      // Store a backup of our userData
      window._simpleAuthUserData = Object.assign({}, window.userData);

      // Set up a watcher to restore userData if it gets cleared
      const watchUserData = () => {
        if (!window.userData || !window.userData.secret) {
          if (this.isLoggedIn() && window._simpleAuthUserData) {
            console.log('SimpleAuth: Restoring userData after external modification');
            window.userData = Object.assign({}, window._simpleAuthUserData);
            window.user = window.userData;
            window.userFullyLoaded = true;
            syncHeaderAuthControls(window.userData);
            document.dispatchEvent(new CustomEvent('user-ready', { detail: window.userData }));
            if (typeof window.$ !== 'undefined' && window.$ && window.$.fn) {
              try { window.$(document).trigger('user-ready', window.userData); } catch (_) {
                // jQuery not available, skip trigger
              }
            }
          }
        }
      };

      // Check every 2 seconds for userData tampering
      setInterval(watchUserData, 2000);
    }
  }
};

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () {
    // Wait a bit for any Firebase to initialize first, then override it
    setTimeout(() => {
      safeCallSimpleAuth('forceInit');
    }, 50);

    // Also ensure our auth system takes precedence after page load
    window.addEventListener('load', function () {
      setTimeout(() => {
        safeCallSimpleAuth('forceInit');
      }, 100);
    });
  });
} else {
  // DOM is already loaded
  setTimeout(() => {
    safeCallSimpleAuth('forceInit');
  }, 50);

  // Also ensure our auth system takes precedence after page load
  window.addEventListener('load', function () {
    setTimeout(() => {
      safeCallSimpleAuth('forceInit');
    }, 100);
  });
}

// Global logout function for compatibility
function signout() {
  safeCallSimpleAuth('logout');
}

// Helper function for getting user data (compatibility with existing code)
function getUser(user, callback) {
  if (typeof window !== 'undefined' && window.SimpleAuth && typeof window.SimpleAuth.isLoggedIn === 'function' && window.SimpleAuth.isLoggedIn()) {
    const userData =
      typeof window.SimpleAuth.getCurrentUser === 'function'
        ? window.SimpleAuth.getCurrentUser()
        : null;
    if (typeof callback === 'function') {
      callback(userData);
    }
    return userData;
  } else {
    const fallbackUserData = {
      id: 'test-user-id',
      secret: 'test-secret',
      email: 'test@example.com',
      name: 'Test User',
      is_subscribed: false
    };

    if (typeof callback === 'function') {
      callback(fallbackUserData);
    }
    return fallbackUserData;
  }
}

// Expose helper functions globally for legacy modules
// Some modules (e.g., datasets.js, account.js) call `getUser` directly
// Ensure it is available on the window object after bundling
if (typeof window !== 'undefined') {
  window.getUser = window.getUser || getUser;
  window.signout = window.signout || signout;
}
